import { isDriveFolderBackingNotice } from "../../utils/driveFolder";
import { makeRequest, API_BASE_URL, axios, getValidNoticeType } from "./client";
import type { DashboardStats } from "./types";
import {
  fileToDataUrl,
  preprocessOrgBannerDataUrl,
  ORGANIZATION_LOGO_UPLOAD_OPTIONS,
  processImageForUpload,
  shouldProcessNoticeImage,
} from "../../utils/imageUpload";
import { bucketNoticesByLastNDays } from "../../domain/dashboard/noticeDayBuckets";

// Forward reference — will be set by index.ts to avoid circular deps
let _apiRef: any = null;
export const setApiRef = (ref: any) => { _apiRef = ref; };

/** An organization's last 3 active advertisements, as shown on its public page. */
export interface PublicPageAdvertisement {
  advertisementId: string;
  title: string;
  description: string;
  price: string;
  category: string;
  adType: string;
  imageUrl?: string;
  location?: string;
  createdAt: string;
  expiresAt?: string;
}

export const uploadSchoolLogo = async (file: File, schoolId: string, token: string) => {
  let fileToUpload = file;
  if (shouldProcessNoticeImage(file)) {
    try {
      fileToUpload = await processImageForUpload(
        file,
        ORGANIZATION_LOGO_UPLOAD_OPTIONS
      );
    } catch {
      // Upload original if optimization fails.
    }
  }

  const base64File = await fileToDataUrl(fileToUpload);

  const response = await axios.put(
    `${API_BASE_URL}/v1/org/${schoolId}/account`,
    {
      logo: base64File,
      contentType: fileToUpload.type,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    }
  );

  const logoUrl = response.data.organization?.logo;

  if (!logoUrl || typeof logoUrl !== "string") {
    throw new Error("Failed to get logo URL from server response");
  }

  return {
    logoUrl,
  };
};

export const uploadOrgBanner = async (base64Image: string, schoolId: string, token: string) => {
  const { dataUrl: imageToUpload, contentType } =
    await preprocessOrgBannerDataUrl(base64Image);

  const response = await axios.put(
    `${API_BASE_URL}/v1/org/${schoolId}/account`,
    {
      orgBanner: imageToUpload,
      contentType: contentType,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    }
  );

  const bannerUrl =
    response.data?.organization?.orgBanner ||
    response.data?.organization?.banner ||
    response.data?.orgBanner ||
    response.data?.banner;

  if (!bannerUrl || typeof bannerUrl !== "string") {
    if (response.status === 200) {
      const updatedFields = response.data?.updatedFields || [];
      if (updatedFields.includes("orgBanner")) {
        throw new Error("Banner uploaded successfully but URL not returned. The backend may need to be updated.");
      } else {
        throw new Error("Backend does not support banner uploads yet. Please update the backend code and redeploy.");
      }
    }

    throw new Error("Failed to get banner URL from server response");
  }

  return {
    bannerUrl,
  };
};

export const dashboardApi = {
  getStats: async (
    schoolId: string,
    token: string,
    month?: string
  ): Promise<DashboardStats> => {
    try {
      const params = new URLSearchParams();
      if (month) {
        params.append("month", month);
      }

      const url = `/v1/org/${schoolId}/stats${
        params.toString() ? `?${params.toString()}` : ""
      }`;

      const response = await makeRequest<DashboardStats>(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch {
      const [usersResponse, noticesResponse] = await Promise.all([
        _apiRef.users.getAll(schoolId, token),
        _apiRef.notices.getAll(schoolId, token),
      ]);

      const users = Array.isArray(usersResponse) ? usersResponse : [];
      const notices = Array.isArray(noticesResponse)
        ? noticesResponse.filter(notice => notice.excludeFromFeed !== true && !isDriveFolderBackingNotice(notice.title) && !/^Drive (file|folder) removed:/i.test((notice.title || "").trim()))
        : [];

      const totalUsers = users.length;
      const totalNotices = notices.length;

      const noticesByDay = bucketNoticesByLastNDays(notices, 7);

      const noticesByType = notices.reduce(
        (acc: Record<string, number>, notice: any) => {
          const type = getValidNoticeType(notice.type);
          acc[type] = (acc[type] || 0) + 1;

          if (notice.isUrgent === true) {
            acc["urgent"] = (acc["urgent"] || 0) + 1;
          }

          return acc;
        },
        {}
      );

      const noticesByTypeArray = Object.entries(noticesByType).map(
        ([type, count]) => ({
          type: type.charAt(0).toUpperCase() + type.slice(1),
          count: count as number,
        })
      );

      const totalReadCount = notices.reduce(
        (sum: number, notice: any) => sum + (notice.readCount || 0),
        0
      );
      const noticesReadPercentage =
        totalNotices > 0
          ? Math.round((totalReadCount / totalNotices) * 100)
          : 0;

      return {
        totalNotices,
        totalUsers,
        noticesReadPercentage,
        noticesByDay,
        noticesByType: noticesByTypeArray,
        overallReadRate: noticesReadPercentage,
        readTimePatterns: {
          within1Hour: 0,
          within24Hours: 0,
          after24Hours: 0,
        },
        upcomingEvents: notices.filter((notice: any) => {
          if (!notice.eventStartDate) return false;
          const eventDate = new Date(notice.eventStartDate);
          const now = new Date();
          return eventDate >= now;
        }).length,
      };
    }
  },

  updateAccountDetails: async (
    updateData: {
      schoolId: string;
      name?: string;
      description?: string;
      type?: string;
      logo?: string;
      contentType?: string;
      phoneNumber?: string;
      /** When true, organization phone is omitted from mobile app API responses. */
      hidePhoneInApp?: boolean;
      website?: string;
      isPublic?: boolean;
      showContactInfo?: boolean;
      publicPageSettings?: {
        tagline?: string;
        showPhone?: boolean;
        showEmail?: boolean;
        showWebsite?: boolean;
        showShopPosts?: boolean;
        showParentCount?: boolean;
        showActiveNoticeCount?: boolean;
        showUpcomingEventsCount?: boolean;
        themeColorHex?: string;
        // Statistics fields (updated by dashboard)
        parentCount?: number;
        activeNoticeCount?: number;
        upcomingEventsCount?: number;
        // Last 3 active advertisements for public page
        advertisements?: Array<PublicPageAdvertisement>;
      };
      notificationSettings?: {
        eventRemindersEnabled?: boolean;
        eventReminderMode?: string;
        reminderTimeOfDay?: string;
        timezone?: string;
      };
    },
    token: string
  ): Promise<{
    message: string;
    school: {
      schoolId: string;
      schoolName: string;
      schoolDescription: string;
      schoolType: string;
      schoolLogo: string;
      phoneNumber?: string;
      website?: string;
      isCircleOpen: boolean;
      email: string;
      createdAt: string;
      updatedAt: string;
    };
    organization: {
      oid: string;
      name?: string;
      description?: string;
      logo?: string;
      logoContentType?: string;
      type?: string;
      phoneNumber?: string;
      hidePhoneInApp?: boolean;
      website?: string;
      isPublic?: string;
      showContactInfo?: string;
      publicPageSettings?: {
        tagline?: string;
        showPhone?: boolean;
        showEmail?: boolean;
        showWebsite?: boolean;
        showShopPosts?: boolean;
        showParentCount?: boolean;
        showActiveNoticeCount?: boolean;
        showUpcomingEventsCount?: boolean;
        themeColorHex?: string;
        // Statistics fields (updated by dashboard)
        parentCount?: number;
        activeNoticeCount?: number;
        upcomingEventsCount?: number;
        // Last 3 active advertisements for public page
        advertisements?: Array<PublicPageAdvertisement>;
      };
      notificationSettings?: {
        eventRemindersEnabled?: boolean;
        eventReminderMode?: string;
        reminderTimeOfDay?: string;
        timezone?: string;
      };
      soid?: string;
    };
    changes: Record<string, { from: any; to: any }>;
  }> => {
    const response = await makeRequest<{
      message: string;
      oid: string;
      updatedFields: string[];
      organization: {
        oid: string;
        name?: string;
        description?: string;
        logo?: string;
        logoContentType?: string;
        type?: string;
        phoneNumber?: string;
        hidePhoneInApp?: boolean;
        website?: string;
        isPublic?: string;
        showContactInfo?: string;
        publicPageSettings?: {
          tagline?: string;
          showPhone?: boolean;
          showEmail?: boolean;
          showWebsite?: boolean;
          showShopPosts?: boolean;
          showParentCount?: boolean;
          showActiveNoticeCount?: boolean;
          showUpcomingEventsCount?: boolean;
          themeColorHex?: string;
        };
        notificationSettings?: {
          eventRemindersEnabled?: boolean;
          eventReminderMode?: string;
          reminderTimeOfDay?: string;
          timezone?: string;
        };
        soid?: string;
      };
    }>(`/v1/org/${updateData.schoolId}/account`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        name: updateData.name,
        description: updateData.description,
        type: updateData.type,
        logo: updateData.logo,
        contentType: updateData.contentType,
        phoneNumber: updateData.phoneNumber,
        hidePhoneInApp: updateData.hidePhoneInApp,
        website: updateData.website,
        isPublic: updateData.isPublic,
        showContactInfo: updateData.showContactInfo,
        publicPageSettings: updateData.publicPageSettings,
        notificationSettings: updateData.notificationSettings,
      }),
    });

    const result: {
      message: string;
      school: {
        schoolId: string;
        schoolName: string;
        schoolDescription: string;
        schoolType: string;
        schoolLogo: string;
        phoneNumber?: string;
        website?: string;
        isCircleOpen: boolean;
        email: string;
        createdAt: string;
        updatedAt: string;
      };
      organization: {
        oid: string;
        name?: string;
        description?: string;
        logo?: string;
        logoContentType?: string;
        type?: string;
        phoneNumber?: string;
        hidePhoneInApp?: boolean;
        website?: string;
        isPublic?: string;
        showContactInfo?: string;
        publicPageSettings?: {
          tagline?: string;
          showPhone?: boolean;
          showEmail?: boolean;
          showWebsite?: boolean;
          showShopPosts?: boolean;
          showParentCount?: boolean;
          showActiveNoticeCount?: boolean;
          showUpcomingEventsCount?: boolean;
          themeColorHex?: string;
        };
        notificationSettings?: {
          eventRemindersEnabled?: boolean;
          eventReminderMode?: string;
          reminderTimeOfDay?: string;
          timezone?: string;
        };
        soid?: string;
      };
      changes: Record<string, { from: any; to: any }>;
    } = {
      message: response.message,
      school: {
        schoolId: response.oid,
        schoolName: response.organization?.name || updateData.name || "",
        schoolDescription:
          response.organization?.description || updateData.description || "",
        schoolType:
          response.organization?.type || updateData.type || "organization",
        schoolLogo: response.organization?.logo || updateData.logo || "",
        phoneNumber: response.organization?.phoneNumber || updateData.phoneNumber,
        website: response.organization?.website || updateData.website,
        isCircleOpen: false,
        email: "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      organization: response.organization,
      changes: {},
    };
    return result;
  },
};
